import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  ArrowRight, 
  CheckCircle2, 
  Building2, 
  Users, 
  Award, 
  Clock,
  TrendingUp,
  Shield,
  Wrench,
  Target
} from 'lucide-react';
import { companyInfo, sectors, recentProjects, testimonials } from '../utils/mockData';
import HeroSection from '@/components/Hero';
export const Home = () => {
  const observerRef = useRef(null);

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = document.querySelectorAll('.fade-in-section');
    elements.forEach((el) => observerRef.current.observe(el));

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  return (
    <div className="min-h-screen">
      <HeroSection/>

      <section className="py-16 bg-white relative mt-2 z-20">
        <div className="container mx-auto px-4">
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl shadow-2xl p-8 md:p-12">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {companyInfo.stats.map((stat, index) => (
                <div key={index} className="text-center fade-in-section">
                  <div className="text-4xl md:text-5xl font-bold text-blue-400 mb-2">
                    {stat.value}
                  </div>
                  <div className="text-gray-300 text-sm md:text-base">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section id="about" className="py-8 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="fade-in-section">
              <Badge className="mb-4 bg-blue-100 text-blue-700 hover:bg-blue-100">About BlueWater</Badge>
              <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
                Building Tomorrow's Landmarks
              </h2>
              <p className="text-gray-700 mb-6 leading-relaxed text-lg">
                {companyInfo.aboutExtended}
              </p>
              <div className="space-y-3 mb-8">
                {[
                  'ISO 9001:2015 Certified Quality Management',
                  'Award-Winning Project Delivery',
                  'Sustainable Building Practices',
                  'Health & Safety Excellence'
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <CheckCircle2 className="text-blue-600 flex-shrink-0" size={20} />
                    <span className="text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
              <Link to="/contact">
                <Button className="bg-slate-900 hover:bg-slate-800 text-white">
                  Learn More About Us
                  <ArrowRight className="ml-2" size={18} />
                </Button>
              </Link>
            </div>
            <div className="relative fade-in-section">
              <div className="grid grid-cols-2 gap-4">
                <img 
                  src="https://images.unsplash.com/photo-1718209962486-4f91ce71886b" 
                  alt="Construction Team" 
                  className="rounded-lg shadow-lg w-full h-64 object-cover hover:scale-105 transition-transform duration-300"
                />
                <img 
                  src="https://images.pexels.com/photos/33185890/pexels-photo-33185890.jpeg" 
                  alt="Construction Work" 
                  className="rounded-lg shadow-lg w-full h-64 object-cover mt-8 hover:scale-105 transition-transform duration-300"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-blue-600 rounded-lg -z-10"></div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12 fade-in-section">
            <Badge className="mb-4 bg-blue-100 text-blue-700 hover:bg-blue-100">Why Choose BlueWater</Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              Your Trusted Construction Partner
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto text-lg">
              We combine expertise, innovation, and dedication to deliver exceptional results
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Shield, title: 'Safety First', desc: 'Industry-leading health and safety standards on every project' },
              { icon: Award, title: 'Quality Assured', desc: 'Award-winning craftsmanship with ISO certifications' },
              { icon: Clock, title: 'On-Time Delivery', desc: 'Proven track record of meeting deadlines and budgets' },
              { icon: Users, title: 'Expert Team', desc: '150+ skilled professionals with decades of experience' }
            ].map((feature, index) => (
              <Card key={index} className="fade-in-section hover:shadow-xl transition-shadow duration-300 border-0 shadow-md">
                <CardContent className="p-6 text-center">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <feature.icon className="text-blue-600" size={32} />
                  </div>
                  <h3 className="font-bold text-xl mb-2 text-slate-900">{feature.title}</h3>
                  <p className="text-gray-600">{feature.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Sectors Section */}
      <section id="sectors" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12 fade-in-section">
            <Badge className="mb-4 bg-blue-100 text-blue-700 hover:bg-blue-100">Our Sectors</Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              Comprehensive Construction Solutions
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto text-lg">
              Delivering excellence across multiple sectors with specialized expertise
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {sectors.map((sector, index) => (
              <Card key={sector.id} className="fade-in-section overflow-hidden group hover:shadow-2xl transition-all duration-300 border-0 shadow-lg">
                <div className="relative h-64 overflow-hidden">
                  <img 
                    src={sector.image} 
                    alt={sector.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 to-transparent"></div>
                  <div className="absolute bottom-4 left-4 right-4">
                    <h3 className="text-2xl font-bold text-white mb-2">{sector.title}</h3>
                  </div>
                </div>
                <CardContent className="p-6">
                  <p className="text-gray-700 mb-4">{sector.description}</p>
                  <div className="flex flex-wrap gap-2">
                    {sector.services.map((service, idx) => (
                      <Badge key={idx} variant="outline" className="text-xs">
                        {service}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Recent Projects Section */}
      <section id="projects" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12 fade-in-section">
            <Badge className="mb-4 bg-blue-100 text-blue-700 hover:bg-blue-100">Recent Projects</Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              Our Latest Achievements
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto text-lg">
              Showcasing our commitment to excellence through completed projects
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-2 gap-8">
            {recentProjects.map((project) => (
              <Card key={project.id} className="fade-in-section overflow-hidden group hover:shadow-2xl transition-all duration-300 border-0 shadow-lg">
                <div className="relative h-80 overflow-hidden">
                  <img 
                    src={project.image} 
                    alt={project.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute top-4 right-4">
                    <Badge className="bg-blue-600 text-white">{project.category}</Badge>
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </div>
                <CardContent className="p-6">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h3 className="text-2xl font-bold text-slate-900 mb-2">{project.title}</h3>
                      <p className="text-sm text-gray-600">{project.location} • {project.year}</p>
                    </div>
                    <Badge variant="outline" className="text-blue-600 border-blue-600">{project.value}</Badge>
                  </div>
                  <p className="text-gray-700">{project.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12 fade-in-section">
            <Badge className="mb-4 bg-blue-100 text-blue-700 hover:bg-blue-100">Client Testimonials</Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              What Our Clients Say
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto text-lg">
              Don't just take our word for it - hear from our satisfied clients
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="fade-in-section hover:shadow-xl transition-shadow duration-300 border-0 shadow-md">
                <CardContent className="p-6">
                  <div className="flex gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <svg key={i} className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    ))}
                  </div>
                  <p className="text-gray-700 mb-6 italic">"{testimonial.content}"</p>
                  <div className="border-t pt-4">
                    <p className="font-bold text-slate-900">{testimonial.name}</p>
                    <p className="text-sm text-gray-600">{testimonial.role}</p>
                    <p className="text-sm text-blue-600">{testimonial.company}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-slate-800 to-slate-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-blue-500 rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-700 rounded-full filter blur-3xl"></div>
        </div>
        <div className="container mx-auto px-4 text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Start Your Project?
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Let's discuss how we can bring your vision to life with our expertise and dedication
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 text-lg shadow-xl">
                Get in Touch
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
            <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-slate-900 px-8 py-6 text-lg">
              Request a Quote
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};
